import { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useConsumableBalances, useLoadCredits, useLifetimeConsumedPreview } from '../queries/consumableBalances'
import type { MessageType } from '../types/consumableBalance.types'
import type { LiveCampaignEventCounts } from '@/features/developer-console/types/balanceInspector.types'

interface Props {
  accountId: string
  /** Pre-selected channel when opened from a specific balance row's "Load Credits" button. */
  defaultChannel?: string
  onClose: () => void
}

// Matches AdjustBalanceModal's channel set.
const CHANNEL_OPTIONS = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'RCS', label: 'RCS' },
  { value: 'WEB_PUSH', label: 'Web Push' },
  { value: 'MOBILE_PUSH', label: 'Mobile Push' },
]

const MESSAGE_TYPE_OPTIONS: { value: MessageType; label: string }[] = [
  { value: 'promotional', label: 'Promotional' },
  { value: 'transactional', label: 'Transactional' },
]

/** Mirrors ConsumableBalanceController#resolveConsumed exactly — web push/mobile push have no
 *  promotional/transactional split in campaign_events, so the channel-level total is used for
 *  whichever pool is selected. */
function resolveLiveConsumed(
  live: LiveCampaignEventCounts | undefined,
  channel: string,
  messageType: MessageType,
): number | undefined {
  if (!live) return undefined
  switch (channel) {
    case 'EMAIL':
      return messageType === 'promotional' ? live.email.promotional : live.email.transactional
    case 'SMS':
      return messageType === 'promotional' ? live.sms.promotional : live.sms.transactional
    case 'WHATSAPP':
      return messageType === 'promotional' ? live.whatsapp.promotional : live.whatsapp.transactional
    case 'RCS':
      return messageType === 'promotional' ? live.rcs.promotional : live.rcs.transactional
    case 'WEB_PUSH':
      return live.webPushSent
    case 'MOBILE_PUSH':
      return live.mobilePushSent
    default:
      return undefined
  }
}

export function LoadCreditsModal({ accountId, defaultChannel, onClose }: Props) {
  const [channel, setChannel] = useState(defaultChannel ?? '')
  const [messageType, setMessageType] = useState<MessageType>('promotional')
  const [credits, setCredits] = useState('')
  const [reference, setReference] = useState('')

  const mutation = useLoadCredits(accountId)
  const { data: balances } = useConsumableBalances(accountId)
  const livePreview = useLifetimeConsumedPreview(accountId)

  const creditsValue = Number(credits)
  const isValid = !!channel && credits.trim() !== '' && !Number.isNaN(creditsValue) && creditsValue > 0

  const currentBalance = balances?.find((b) => b.channel === channel)
  const existingLoaded =
    (messageType === 'promotional'
      ? currentBalance?.totalCreditsLoadedPromo
      : currentBalance?.totalCreditsLoadedTransactional) ?? 0
  const liveConsumed = channel ? resolveLiveConsumed(livePreview.data, channel, messageType) : undefined
  const projectedAvailable =
    liveConsumed !== undefined && isValid ? existingLoaded + creditsValue - liveConsumed : undefined

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    mutation.mutate(
      {
        channel,
        messageType,
        credits: creditsValue,
        reference: reference.trim() || undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Load Credits</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Adds to the lifetime total loaded, recomputes total consumed from a live campaign_events query, and
              recalculates the available balance — this also self-corrects any drift since the last load.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Channel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Channel <span className="text-red-500">*</span>
            </label>
            <Select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              options={CHANNEL_OPTIONS}
              placeholder="Select a channel"
              required
              disabled={!!defaultChannel}
            />
          </div>

          {/* Message type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Balance Pool <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {MESSAGE_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="messageType"
                    value={opt.value}
                    checked={messageType === opt.value}
                    onChange={() => setMessageType(opt.value)}
                    className="accent-primary"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
            {(channel === 'WEB_PUSH' || channel === 'MOBILE_PUSH') && (
              <p className="text-xs text-amber-600 mt-1">
                {CHANNEL_OPTIONS.find((c) => c.value === channel)?.label} has no promotional/transactional split in
                campaign_events — the channel-level live total is used as consumed for whichever pool you load here.
              </p>
            )}
          </div>

          {/* Credits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credits to Load <span className="text-red-500">*</span>
            </label>
            <Input
              type="number"
              min="1"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="e.g. 50000"
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              Always positive — this adds to the lifetime total loaded. To correct an over-load, use Adjust Balance
              instead.
            </p>
          </div>

          {/* Live preview */}
          {channel && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
              {livePreview.isLoading ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Calculating live consumption from campaign_events…
                </div>
              ) : livePreview.isError ? (
                <p className="text-amber-600">
                  Couldn't load a live preview — this won't block the load itself, it's just calculated fresh at
                  submit time too.
                </p>
              ) : (
                <div className="space-y-1">
                  <div className="flex justify-between text-gray-600">
                    <span>Live consumed to date ({messageType})</span>
                    <span className="font-mono">{liveConsumed?.toLocaleString() ?? '—'}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Current total loaded ({messageType})</span>
                    <span className="font-mono">{existingLoaded.toLocaleString()}</span>
                  </div>
                  {projectedAvailable !== undefined && (
                    <div className="flex justify-between font-medium text-gray-900 pt-1 border-t border-gray-200 mt-1">
                      <span>Projected available after this load</span>
                      <span className="font-mono">{projectedAvailable.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Optional PO / invoice / ticket number"
            />
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">
              {(mutation.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? 'Loading…' : 'Load Credits'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
